import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Button, TableContainer, Typography } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import PageTitle from '../../components/PageTitle/PageTitle';
import { ProjectStatus } from '../../enums/projectStatus';
import { DashboardSummary } from '../../models/dashboardSummary';
import { HeadCell } from '../../models/headCell';
import { Project } from '../../models/project';
import { EDIT_PROJECT, PROJECTS } from '../../router/constants';
import { dashboardService } from '../../services/dashboardService';
import { projectService } from '../../services/projectService';
import DeleteModal from '../Projects/DeleteModal';
import { initialProjectsColumnsData } from '../Projects/ProjectsList';
import NotificationsList from './NotificationsList';

const Dashboard = (): JSX.Element => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalData, setDeleteModalData] = useState<{
    open: boolean;
    project?: Project;
  }>({ open: false });
  const [columnsData] = useState<HeadCell[]>([...initialProjectsColumnsData]);
  const [notifications, setNotifications] = useState<DashboardSummary>({
    declinedProjects: 0,
    expiredProjects: 0,
    reviewInvoices: 0
  });

  useEffect(() => {
    getProjects();
    getNotifications();
  }, []);

  const getProjects = () => {
    projectService
      .getProjects({}, { params: { page: 0, size: 5 } })
      .then((response) => {
        setProjects(response.data.content);
        setLoading(false);
      });
  };

  const onDeleteProject = (
    event: React.MouseEvent<unknown>,
    project: Project
  ) => {
    event.stopPropagation();
    setDeleteModalData({ open: true, project });
  };

  const confirmDeleteProject = () => {
    setDeleteModalData({ open: false, project: undefined });
    if (deleteModalData.project?.id) {
      projectService.deleteProject(deleteModalData.project.id).then(() => {
        getProjects();
      });
    }
  };

  const onEditProject = (
    event: React.MouseEvent<unknown>,
    project: Project
  ) => {
    event.stopPropagation();
    navigate(`${EDIT_PROJECT}/${project.id}`);
  };

  const onShowActionIcons = (project: Project) => {
    return ProjectStatus[project.status] !== ProjectStatus.COMPLETED;
  };

  const getNotifications = () => {
    dashboardService.getDashboardSummary().then((response) => {
      if (response.data) {
        setNotifications(response.data);
      }
    });
  };

  return (
    <>
      <PageTitle>Welcome back, {keycloak.tokenParsed?.given_name}</PageTitle>
      <NotificationsList notification={notifications} />
      <TableContainer sx={{ minHeight: '160px' }}>
        <DefaultTable
          columns={columnsData}
          data={projects}
          rowClick={(project: Project) => navigate(`${PROJECTS}/${project.id}`)}
          actionProps={{
            onEdit: onEditProject,
            onDelete: onDeleteProject,
            showEditButton: onShowActionIcons,
            showDeleteButton: onShowActionIcons,
            style: { padding: '12px' }
          }}
        />
        {!loading && !projects?.length && (
          <Typography variant="h5" align="center" m={4}>
            No projects found!
          </Typography>
        )}
      </TableContainer>
      <Button
        variant="lightBlue"
        sx={{ mt: 5, width: '200px' }}
        endIcon={<ArrowRightAltIcon />}
        onClick={() => navigate(PROJECTS)}
      >
        View All
      </Button>
      <DeleteModal
        modalData={deleteModalData}
        closeModal={() => setDeleteModalData({ open: false })}
        confirmModal={() => confirmDeleteProject()}
      />
    </>
  );
};

export default Dashboard;
